import { ComponentType, lazy, Suspense } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import DashboardLayout from "./layouts/dashboard";
import SimpleLayout from "./layouts/simple";
import Loader from "./components/Loader";
import { LoginPage } from "./pages/login";
import { RegisterPage } from "./pages/login/register";
import { ForgotPassword } from "./pages/login/forgotPassword";
import { VerifyClient } from "./pages/login/verifyClient";
import { App, Access, Role } from "./models";
import { hasSalesDashboardAccess } from "./pages/salesDashboard/lib";

// Route-level code splitting. Layouts and the login flow stay eagerly imported
// because they are needed on first paint; every other page is loaded on demand
// so the login screen no longer downloads the whole ERP (apexcharts, exceljs,
// html2pdf, FullCalendar, the email editor, the spreadsheet importer, ...).
//
// lazyNamed() adapts the page barrels, which re-export named components and
// have no default export, to the { default } shape React.lazy expects.
function lazyNamed<T, K extends keyof T>(loader: () => Promise<T>, name: K) {
  return lazy(() =>
    loader().then((module) => ({
      default: module[name] as unknown as ComponentType<any>,
    })),
  );
}

const DashboardPage = lazy(() => import("./pages/dashboard"));
const Feedbacks = lazy(() => import("./pages/feedback/List"));
const NewFeedback = lazy(() => import("./pages/feedback/New"));
const Account = lazy(() => import("./pages/account/List"));
const ViewAccount = lazyNamed(() => import("./pages/account"), "View");
const Printers = lazy(() => import("./pages/Printers"));
const Voucher = lazy(() => import("./pages/voucher"));
const Settings = lazy(() => import("./pages/settings"));
const Wallet = lazy(() => import("./pages/wallet"));
const WhatsAppMarketing = lazy(() => import("./pages/whatsappMarketing"));
const EmailMarketing = lazy(() => import("./pages/emailMarketing"));
const Page404 = lazy(() => import("./pages/404"));
const Pricing = lazy(() => import("./pages/pricing"));

const Profile = lazyNamed(() => import("./pages/user"), "Profile");
const ViewUser = lazyNamed(() => import("./pages/user"), "View");
const UserList = lazyNamed(() => import("./pages/user"), "List");
const AddUser = lazyNamed(() => import("./pages/user"), "Add");
const EditUser = lazyNamed(() => import("./pages/user"), "Edit");

const StoreList = lazyNamed(() => import("./pages/stores"), "List");
const AddStore = lazyNamed(() => import("./pages/stores"), "Add");
const EditStore = lazyNamed(() => import("./pages/stores"), "Edit");

const CategoryList = lazyNamed(() => import("./pages/category"), "List");
const AddCategory = lazyNamed(() => import("./pages/category"), "Add");
const EditCategory = lazyNamed(() => import("./pages/category"), "Edit");

const ProductList = lazyNamed(() => import("./pages/product"), "List");
const AddProduct = lazyNamed(() => import("./pages/product"), "Add");
const EditProduct = lazyNamed(() => import("./pages/product"), "Edit");
const RecipeCosting = lazy(() => import("./pages/product/Costing/Report"));
const RecipeCard = lazy(() => import("./pages/product/Costing/RecipeCard"));
const DeliveryPricing = lazy(
  () => import("./pages/menuPricing/DeliveryPricing"),
);

const PurchaseList = lazyNamed(() => import("./pages/purchase"), "List");
const AddPurchase = lazyNamed(() => import("./pages/purchase"), "Add");
const EditPurchase = lazyNamed(() => import("./pages/purchase"), "Edit");

const SaleList = lazyNamed(() => import("./pages/sale"), "List");
const AddSale = lazyNamed(() => import("./pages/sale"), "Add");
const EditSale = lazyNamed(() => import("./pages/sale"), "Edit");

const OrderList = lazyNamed(() => import("./pages/order"), "List");
const AddOrder = lazyNamed(() => import("./pages/order"), "Add");
const EditOrder = lazyNamed(() => import("./pages/order"), "Edit");

const InvoiceList = lazyNamed(() => import("./pages/invoice"), "List");
const AddInvoice = lazyNamed(() => import("./pages/invoice"), "Add");
const EditInvoice = lazyNamed(() => import("./pages/invoice"), "Edit");

const QuotationList = lazyNamed(() => import("./pages/quotation"), "List");
const AddQuotation = lazyNamed(() => import("./pages/quotation"), "Add");
const EditQuotation = lazyNamed(() => import("./pages/quotation"), "Edit");

const ViewSupplier = lazyNamed(() => import("./pages/supplier"), "View");
const SupplierList = lazyNamed(() => import("./pages/supplier"), "List");
const AddSupplier = lazyNamed(() => import("./pages/supplier"), "Add");
const EditSupplier = lazyNamed(() => import("./pages/supplier"), "Edit");

const ViewCustomer = lazyNamed(() => import("./pages/customer"), "View");
const CustomerList = lazyNamed(() => import("./pages/customer"), "List");
const AddCustomer = lazyNamed(() => import("./pages/customer"), "Add");
const EditCustomer = lazyNamed(() => import("./pages/customer"), "Edit");

const WastageList = lazyNamed(() => import("./pages/wastage"), "List");
const AddWastage = lazyNamed(() => import("./pages/wastage"), "Add");
const EditWastage = lazyNamed(() => import("./pages/wastage"), "Edit");

const ExpenseList = lazyNamed(() => import("./pages/expense"), "List");
const EditExpense = lazyNamed(() => import("./pages/expense"), "Edit");

const IncomeList = lazyNamed(() => import("./pages/income"), "List");
const EditIncome = lazyNamed(() => import("./pages/income"), "Edit");

const IECategoryList = lazyNamed(() => import("./pages/ieCategory"), "List");
const AddIECategory = lazyNamed(() => import("./pages/ieCategory"), "Add");
const EditIECategory = lazyNamed(() => import("./pages/ieCategory"), "Edit");
const ViewIECategory = lazyNamed(() => import("./pages/ieCategory"), "View");

const FinanceList = lazyNamed(() => import("./pages/finance"), "List");
const AddFinanceLoan = lazyNamed(() => import("./pages/finance"), "Add");
const EditFinanceLoan = lazyNamed(() => import("./pages/finance"), "Edit");

const CounterClosingList = lazyNamed(
  () => import("./pages/accounts/counterClosing"),
  "List",
);
const AddCounterClosing = lazyNamed(
  () => import("./pages/accounts/counterClosing"),
  "Add",
);
const EditCounterClosing = lazyNamed(
  () => import("./pages/accounts/counterClosing"),
  "Edit",
);

const SalesDashboard = lazy(() => import("./pages/salesDashboard"));
const CounterList = lazyNamed(() => import("./pages/counter"), "List");
const AddCounter = lazyNamed(() => import("./pages/counter"), "Add");
const EditCounter = lazyNamed(() => import("./pages/counter"), "Edit");
const CashierShiftList = lazyNamed(
  () => import("./pages/cashierSession"),
  "List",
);
const OpenCashierShift = lazyNamed(
  () => import("./pages/cashierSession"),
  "Open",
);
const CloseCashierShift = lazyNamed(
  () => import("./pages/cashierSession"),
  "Close",
);
const ViewCashierShift = lazyNamed(
  () => import("./pages/cashierSession"),
  "View",
);

const StockList = lazyNamed(() => import("./pages/stock"), "List");
const AddStock = lazyNamed(() => import("./pages/stock"), "Add");
const EditStock = lazyNamed(() => import("./pages/stock"), "Edit");
const StockCountList = lazyNamed(() => import("./pages/stockCount"), "List");
const StockCountSheet = lazyNamed(() => import("./pages/stockCount"), "Sheet");
const StockCountView = lazyNamed(() => import("./pages/stockCount"), "View");
const FoodCostReport = lazy(() => import("./pages/foodCost/Report"));

const ViewBranch = lazyNamed(() => import("./pages/branch"), "View");
const BranchList = lazyNamed(() => import("./pages/branch"), "List");
const AddBranch = lazyNamed(() => import("./pages/branch"), "Add");
const EditBranch = lazyNamed(() => import("./pages/branch"), "Edit");

const Accounts = lazyNamed(() => import("./pages/accounts"), "Accounts");
const Txns = lazyNamed(() => import("./pages/accounts"), "Txns");
const DayBook = lazyNamed(() => import("./pages/accounts"), "DayBook");
const Ledger = lazyNamed(() => import("./pages/accounts"), "Ledger");
const BalanceSheet = lazyNamed(
  () => import("./pages/accounts"),
  "BalanceSheet",
);
const TrialBalance = lazyNamed(
  () => import("./pages/accounts"),
  "TrialBalance",
);
const ProfitAndLoss = lazyNamed(
  () => import("./pages/accounts"),
  "ProfitAndLoss",
);
const GSTFiling = lazyNamed(() => import("./pages/accounts"), "GSTFiling");

const Restaurant = lazy(() => import("./pages/restaurant/Dashboard"));
const RestaurantStore = lazy(() => import("./pages/restaurant/Store"));
const OutdoorMenu = lazy(() => import("./pages/restaurant/OutdoorMenu"));
const Vessel = lazy(() => import("./pages/restaurant/Vessel"));

const ViewTable = lazyNamed(() => import("./pages/restaurant/table"), "View");
const TableList = lazyNamed(() => import("./pages/restaurant/table"), "List");
const AddTable = lazyNamed(() => import("./pages/restaurant/table"), "Add");
const EditTable = lazyNamed(() => import("./pages/restaurant/table"), "Edit");

const TableOrderList = lazyNamed(
  () => import("./pages/restaurant/order"),
  "List",
);
const TableAddOrder = lazyNamed(
  () => import("./pages/restaurant/order"),
  "Add",
);
const TableEditOrder = lazyNamed(
  () => import("./pages/restaurant/order"),
  "Edit",
);

const ViewKitchen = lazyNamed(
  () => import("./pages/restaurant/kitchen"),
  "View",
);
const KitchenList = lazyNamed(
  () => import("./pages/restaurant/kitchen"),
  "List",
);
const AddKitchen = lazyNamed(() => import("./pages/restaurant/kitchen"), "Add");
const EditKitchen = lazyNamed(
  () => import("./pages/restaurant/kitchen"),
  "Edit",
);

const OutdoorOrderList = lazyNamed(
  () => import("./pages/restaurant/outdoorOrder"),
  "List",
);
const AddOutdoorOrder = lazyNamed(
  () => import("./pages/restaurant/outdoorOrder"),
  "Add",
);
const EditOutdoorOrder = lazyNamed(
  () => import("./pages/restaurant/outdoorOrder"),
  "Edit",
);
const OutdoorQuotationList = lazyNamed(
  () => import("./pages/restaurant/outdoorQuotation"),
  "List",
);
const AddOutdoorQuotation = lazyNamed(
  () => import("./pages/restaurant/outdoorQuotation"),
  "Add",
);
const EditOutdoorQuotation = lazyNamed(
  () => import("./pages/restaurant/outdoorQuotation"),
  "Edit",
);

const TransferList = lazyNamed(() => import("./pages/transfer"), "List");
const AddTransfer = lazyNamed(() => import("./pages/transfer"), "Add");

const UsageList = lazyNamed(() => import("./pages/usage"), "List");
const AddUsage = lazyNamed(() => import("./pages/usage"), "Add");
const EditUsage = lazyNamed(() => import("./pages/usage"), "Edit");

export interface IRouter {
  app?: string;
  role: string;
  isBranch: boolean;
  isActiveClient: boolean;
  access: Array<string>;
}
export default ({
  app = App.APP,
  role,
  isBranch,
  isActiveClient,
  access = [],
}: IRouter) => {
  const routes = useRoutes([
    ...(role?.length > 0
      ? [
          {
            path: "/",
            element: <DashboardLayout />,
            children: [
              { element: <Navigate to="/dashboard" />, index: true },
              ...(isActiveClient
                ? [
                    { path: "dashboard", element: <DashboardPage /> },
                    { path: "profile", element: <Profile /> },

                    ...(app !== App.APP
                      ? [
                          ...(access.includes(Access.CATEGORY)
                            ? [
                                { path: "category", element: <CategoryList /> },
                                {
                                  path: "category/edit/:id",
                                  element: <EditCategory />,
                                },
                              ]
                            : []),
                          ...(access.includes(Access.PRODUCT)
                            ? [
                                { path: "product", element: <ProductList /> },
                                {
                                  path: "product/edit/:id",
                                  element: <EditProduct />,
                                },
                              ]
                            : []),
                          ...(access.includes(Access.PRODUCTION_COSTING) &&
                          (role === Role.SUPERADMIN ||
                            role === Role.ADMIN ||
                            role === Role.MANAGER)
                            ? [
                                {
                                  path: "recipe-costing",
                                  element: <RecipeCosting />,
                                },
                                {
                                  path: "recipe-card/:id",
                                  element: <RecipeCard />,
                                },
                              ]
                            : []),
                          ...(access.includes(Access.DELIVERY_APP_PRICING) &&
                          (role === Role.SUPERADMIN ||
                            role === Role.ADMIN ||
                            role === Role.MANAGER)
                            ? [
                                {
                                  path: "delivery-pricing",
                                  element: <DeliveryPricing />,
                                },
                              ]
                            : []),
                          ...(access.includes(Access.STORE)
                            ? [{ path: "store", element: <StoreList /> }]
                            : []),
                          // ...(access.includes(Access.PURCHASE)
                          //   ? [
                          //       { path: "purchase", element: <PurchaseList /> },
                          //       {
                          //         path: "purchase/add",
                          //         element: <AddPurchase />,
                          //       },
                          //       {
                          //         path: "purchase/edit/:id",
                          //         element: <EditPurchase />,
                          //       },
                          //     ]
                          //   : []),

                          ...(role === Role.SUPERADMIN ||
                          role === Role.ADMIN ||
                          role === Role.MANAGER
                            ? [
                                ...(access.includes(Access.TRANSFER)
                                  ? [
                                      {
                                        path: "transfer",
                                        element: <TransferList />,
                                      },
                                      {
                                        path: "transfer/add",
                                        element: <AddTransfer />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.STOCK)
                                  ? [
                                      { path: "stock", element: <StockList /> },
                                      {
                                        path: "stock/create/:type",
                                        element: <AddStock />,
                                      },
                                      {
                                        path: "stock/edit/:id",
                                        element: <EditStock />,
                                      },
                                    ]
                                  : []),
                              ]
                            : []),

                          ...(access.includes(Access.SUPPLIER)
                            ? [
                                { path: "supplier", element: <SupplierList /> },
                                {
                                  path: "supplier/view/:id",
                                  element: <ViewSupplier />,
                                },
                                {
                                  path: "supplier/add",
                                  element: <AddSupplier />,
                                },
                                {
                                  path: "supplier/edit/:id",
                                  element: <EditSupplier />,
                                },
                              ]
                            : []),

                          ...(access.includes(Access.CUSTOMER)
                            ? [
                                { path: "customer", element: <CustomerList /> },
                                {
                                  path: "customer/view/:id",
                                  element: <ViewCustomer />,
                                },
                                {
                                  path: "customer/add",
                                  element: <AddCustomer />,
                                },
                                {
                                  path: "customer/edit/:id",
                                  element: <EditCustomer />,
                                },
                              ]
                            : []),

                          // ...(access.includes(Access.EXPENSE)
                          //   ? [
                          //       { path: "expense", element: <ExpenseList /> },
                          //       // {
                          //       //   path: "expense/add",
                          //       //   element: <AddExpense />,
                          //       // },
                          //       {
                          //         path: "expense/edit/:id",
                          //         element: <EditExpense />,
                          //       },
                          //     ]
                          //   : []),

                          ...(access.includes(Access.ACCOUNTS) &&
                          (role === Role.SUPERADMIN ||
                            role === Role.ADMIN ||
                            role === Role.MANAGER)
                            ? [
                                { path: "accounts", element: <Accounts /> },
                                {
                                  path: "txns",
                                  element: <Txns />,
                                },
                                ...(access.includes(Access.GST_FILING)
                                  ? [
                                      {
                                        path: "gst-filing",
                                        element: <GSTFiling />,
                                      },
                                    ]
                                  : []),
                                {
                                  path: "day-book",
                                  element: <DayBook />,
                                },
                                {
                                  path: "ledger",
                                  element: <Ledger />,
                                },
                                {
                                  path: "balance-sheet",
                                  element: <BalanceSheet />,
                                },
                                {
                                  path: "trial-balance",
                                  element: <TrialBalance />,
                                },
                                {
                                  path: "profit-and-loss",
                                  element: <ProfitAndLoss />,
                                },
                                {
                                  path: "counter-closing",
                                  element: <CounterClosingList />,
                                },
                                {
                                  path: "counter-closing/add",
                                  element: <AddCounterClosing />,
                                },
                                {
                                  path: "counter-closing/edit/:id",
                                  element: <EditCounterClosing />,
                                },
                              ]
                            : []),

                          // ...(access.includes(Access.CRM) &&
                          // (role === Role.SUPERADMIN ||
                          //   role === Role.ADMIN ||
                          //   role === Role.MANAGER)
                          //   ? [
                          //       {
                          //         path: "whatsapp-marketing",
                          //         element: <WhatsAppMarketing />,
                          //       },
                          //       {
                          //         path: "email-marketing",
                          //         element: <EmailMarketing />,
                          //       },
                          //     ]
                          //   : []),

                          ...(isBranch
                            ? [
                                ...(access.includes(Access.PURCHASE)
                                  ? [
                                      {
                                        path: "purchase",
                                        element: <PurchaseList />,
                                      },
                                      {
                                        path: "purchase/add",
                                        element: <AddPurchase />,
                                      },
                                      {
                                        path: "purchase/edit/:id",
                                        element: <EditPurchase />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.SALE)
                                  ? [
                                      { path: "sale", element: <SaleList /> },
                                      {
                                        path: "sale/add",
                                        element: <AddSale />,
                                      },
                                      {
                                        path: "sale/edit/:id",
                                        element: <EditSale />,
                                      },
                                    ]
                                  : []),
                                ...(hasSalesDashboardAccess(access)
                                  ? [
                                      {
                                        path: "sales-dashboard",
                                        element: <SalesDashboard />,
                                      },
                                      {
                                        path: "counter",
                                        element: <CounterList />,
                                      },
                                      ...(role === Role.SUPERADMIN ||
                                      role === Role.ADMIN ||
                                      role === Role.MANAGER
                                        ? [
                                            {
                                              path: "counter/add",
                                              element: <AddCounter />,
                                            },
                                            {
                                              path: "counter/edit/:id",
                                              element: <EditCounter />,
                                            },
                                          ]
                                        : []),
                                      {
                                        path: "cashier-shift",
                                        element: <CashierShiftList />,
                                      },
                                      {
                                        path: "cashier-shift/open",
                                        element: <OpenCashierShift />,
                                      },
                                      {
                                        path: "cashier-shift/view/:id",
                                        element: <ViewCashierShift />,
                                      },
                                      {
                                        path: "cashier-shift/close/:id",
                                        element: <CloseCashierShift />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.ORDER)
                                  ? [
                                      { path: "order", element: <OrderList /> },
                                      {
                                        path: "order/add",
                                        element: <AddOrder />,
                                      },
                                      {
                                        path: "order/edit/:id",
                                        element: <EditOrder />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.INVOICE)
                                  ? [
                                      {
                                        path: "invoice",
                                        element: <InvoiceList />,
                                      },
                                      {
                                        path: "invoice/add",
                                        element: <AddInvoice />,
                                      },
                                      {
                                        path: "invoice/edit/:id",
                                        element: <EditInvoice />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.QUOTATION)
                                  ? [
                                      {
                                        path: "quotation",
                                        element: <QuotationList />,
                                      },
                                      {
                                        path: "quotation/add",
                                        element: <AddQuotation />,
                                      },
                                      {
                                        path: "quotation/edit/:id",
                                        element: <EditQuotation />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.RESTAURANT)
                                  ? [
                                      {
                                        path: "restaurant",
                                        element: <Restaurant />,
                                      },
                                      { path: "table", element: <TableList /> },
                                      {
                                        path: "kitchen",
                                        element: <KitchenList />,
                                      },
                                      ...(role === Role.SUPERADMIN ||
                                      role === Role.ADMIN ||
                                      role === Role.MANAGER
                                        ? [
                                            {
                                              path: "table/add",
                                              element: <AddTable />,
                                            },
                                            {
                                              path: "table/edit/:id",
                                              element: <EditTable />,
                                            },
                                            {
                                              path: "table/view/:id",
                                              element: <ViewTable />,
                                            },
                                            {
                                              path: "kitchen/add",
                                              element: <AddKitchen />,
                                            },
                                            {
                                              path: "kitchen/edit/:id",
                                              element: <EditKitchen />,
                                            },
                                            {
                                              path: "kitchen/view/:id",
                                              element: <ViewKitchen />,
                                            },
                                            ...(access.includes(
                                              Access.RESTAURANT_STORE,
                                            )
                                              ? [
                                                  {
                                                    path: "restaurant-store",
                                                    element: (
                                                      <RestaurantStore />
                                                    ),
                                                  },
                                                ]
                                              : []),
                                          ]
                                        : []),
                                      {
                                        path: "torder",
                                        element: <TableOrderList />,
                                      },
                                      {
                                        path: "torder/add",
                                        element: <TableAddOrder />,
                                      },
                                      {
                                        path: "torder/edit/:id",
                                        element: <TableEditOrder />,
                                      },

                                      ...(access.includes(Access.OUTDOOR_ORDER)
                                        ? [
                                            {
                                              path: "outdoor-order",
                                              element: <OutdoorOrderList />,
                                            },
                                            {
                                              path: "outdoor-order/add",
                                              element: <AddOutdoorOrder />,
                                            },
                                            {
                                              path: "outdoor-order/edit/:id",
                                              element: <EditOutdoorOrder />,
                                            },
                                            {
                                              path: "outdoor-quotation",
                                              element: <OutdoorQuotationList />,
                                            },
                                            {
                                              path: "outdoor-quotation/add",
                                              element: <AddOutdoorQuotation />,
                                            },
                                            {
                                              path: "outdoor-quotation/edit/:id",
                                              element: <EditOutdoorQuotation />,
                                            },
                                            {
                                              path: "outdoor-menu",
                                              element: <OutdoorMenu />,
                                            },
                                            {
                                              path: "vessel",
                                              element: <Vessel />,
                                            },
                                          ]
                                        : []),
                                    ]
                                  : []),
                                ...(access.includes(Access.EXPENSE)
                                  ? [
                                      {
                                        path: "expense",
                                        element: <ExpenseList />,
                                      },
                                      // {
                                      //   path: "expense/add",
                                      //   element: <AddExpense />,
                                      // },
                                      {
                                        path: "expense/edit/:id",
                                        element: <EditExpense />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.INCOME)
                                  ? [
                                      {
                                        path: "income",
                                        element: <IncomeList />,
                                      },
                                      // {
                                      //   path: "income/add",
                                      //   element: <AddIncome />,
                                      // },
                                      {
                                        path: "income/edit/:id",
                                        element: <EditIncome />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.EXPENSE) ||
                                access.includes(Access.INCOME) ||
                                hasSalesDashboardAccess(access)
                                  ? [
                                      {
                                        path: "ie-category",
                                        element: <IECategoryList />,
                                      },
                                      {
                                        path: "ie-category/add",
                                        element: <AddIECategory />,
                                      },
                                      {
                                        path: "ie-category/edit/:id",
                                        element: <EditIECategory />,
                                      },
                                      {
                                        path: "ie-category/view/:id",
                                        element: <ViewIECategory />,
                                      },
                                    ]
                                  : []),

                                ...(access.includes(Access.WASTAGE)
                                  ? [
                                      {
                                        path: "wastage",
                                        element: <WastageList />,
                                      },
                                      {
                                        path: "wastage/add",
                                        element: <AddWastage />,
                                      },
                                      {
                                        path: "wastage/edit/:id",
                                        element: <EditWastage />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.USAGE)
                                  ? [
                                      { path: "usage", element: <UsageList /> },
                                      {
                                        path: "usage/add",
                                        element: <AddUsage />,
                                      },
                                      {
                                        path: "usage/edit/:id",
                                        element: <EditUsage />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.VOUCHER)
                                  ? [{ path: "voucher", element: <Voucher /> }]
                                  : []),
                                ...(access.includes(Access.FEEDBACK) &&
                                (role === Role.SUPERADMIN ||
                                  role === Role.ADMIN ||
                                  role === Role.MANAGER)
                                  ? [
                                      {
                                        path: "feedbacks",
                                        element: <Feedbacks />,
                                      },
                                    ]
                                  : []),
                                ...(access.includes(Access.FINANCE)
                                  ? [
                                      {
                                        path: "finance",
                                        element: <FinanceList />,
                                      },
                                      ...(role === Role.SUPERADMIN ||
                                      role === Role.ADMIN ||
                                      role === Role.MANAGER ||
                                      role === Role.SUPERVISOR
                                        ? [
                                            {
                                              path: "finance/add",
                                              element: <AddFinanceLoan />,
                                            },
                                            {
                                              path: "finance/edit/:id",
                                              element: <EditFinanceLoan />,
                                            },
                                            {
                                              path: "finance/edit/:id/:typeCode",
                                              element: <EditFinanceLoan />,
                                            },
                                          ]
                                        : []),
                                    ]
                                  : []),

                                ...((access.includes(Access.RESTAURANT) ||
                                  access.includes(Access.OUTDOOR_ORDER) ||
                                  access.includes(Access.SALE) ||
                                  access.includes(Access.ORDER) ||
                                  access.includes(Access.INVOICE) ||
                                  access.includes(Access.QUOTATION)) &&
                                (role === Role.SUPERADMIN ||
                                  role === Role.ADMIN ||
                                  role === Role.MANAGER)
                                  ? [
                                      {
                                        path: "printers",
                                        element: <Printers />,
                                      },
                                    ]
                                  : []),

                                ...(access.includes(Access.ACCOUNT) &&
                                (role === Role.SUPERADMIN ||
                                  role === Role.ADMIN ||
                                  role === Role.MANAGER)
                                  ? [
                                      {
                                        path: "account",
                                        element: <Account />,
                                      },
                                      {
                                        path: "account/view/:id",
                                        element: <ViewAccount />,
                                      },
                                    ]
                                  : []),

                                ...(access.includes(Access.CRM) &&
                                (role === Role.SUPERADMIN ||
                                  role === Role.ADMIN ||
                                  role === Role.MANAGER)
                                  ? [
                                      {
                                        path: "whatsapp-marketing",
                                        element: <WhatsAppMarketing />,
                                      },
                                      {
                                        path: "email-marketing",
                                        element: <EmailMarketing />,
                                      },
                                    ]
                                  : []),

                                ...(access.includes(Access.STOCK_COUNTS) &&
                                (role === Role.SUPERADMIN ||
                                  role === Role.ADMIN ||
                                  role === Role.MANAGER)
                                  ? [
                                      {
                                        path: "stock-count",
                                        element: <StockCountList />,
                                      },
                                      {
                                        path: "stock-count/count",
                                        element: <StockCountSheet />,
                                      },
                                      {
                                        path: "stock-count/count/:id",
                                        element: <StockCountSheet />,
                                      },
                                      {
                                        path: "stock-count/view/:id",
                                        element: <StockCountView />,
                                      },
                                    ]
                                  : []),

                                ...(access.includes(
                                  Access.PRODUCTION_COSTING,
                                ) &&
                                (role === Role.SUPERADMIN ||
                                  role === Role.ADMIN ||
                                  role === Role.MANAGER)
                                  ? [
                                      {
                                        path: "food-cost",
                                        element: <FoodCostReport />,
                                      },
                                    ]
                                  : []),
                              ]
                            : [
                                ...(role === Role.SUPERADMIN ||
                                role === Role.ADMIN ||
                                role === Role.MANAGER
                                  ? [
                                      ...(access.includes(Access.BRANCH)
                                        ? [
                                            {
                                              path: "branch",
                                              element: <BranchList />,
                                            },
                                            {
                                              path: "branch/view/:id",
                                              element: <ViewBranch />,
                                            },
                                            {
                                              path: "branch/add",
                                              element: <AddBranch />,
                                            },
                                            {
                                              path: "branch/edit/:id",
                                              element: <EditBranch />,
                                            },
                                          ]
                                        : []),

                                      ...(access.includes(Access.STORE)
                                        ? [
                                            {
                                              path: "store/add",
                                              element: <AddStore />,
                                            },
                                            {
                                              path: "store/edit/:id",
                                              element: <EditStore />,
                                            },
                                          ]
                                        : []),

                                      ...(access.includes(Access.CATEGORY)
                                        ? [
                                            {
                                              path: "category/add",
                                              element: <AddCategory />,
                                            },
                                          ]
                                        : []),

                                      ...(access.includes(Access.PRODUCT)
                                        ? [
                                            {
                                              path: "product/add",
                                              element: <AddProduct />,
                                            },
                                          ]
                                        : []),
                                    ]
                                  : []),
                              ]),

                          ...(role === Role.SUPERADMIN ||
                          role === Role.ADMIN ||
                          role === Role.MANAGER ||
                          role === Role.SUPERVISOR
                            ? [
                                ...(access.includes(Access.USER)
                                  ? [{ path: "user", element: <UserList /> }]
                                  : []),

                                ...(access.includes(Access.USER) &&
                                (role === Role.SUPERADMIN ||
                                  role === Role.ADMIN ||
                                  role === Role.MANAGER)
                                  ? [
                                      ...(access.includes(Access.PAYROLL) ||
                                      access.includes(Access.HISTORY)
                                        ? [
                                            {
                                              path: "user/view/:id",
                                              element: <ViewUser />,
                                            },
                                          ]
                                        : []),

                                      ...(!isBranch
                                        ? [
                                            {
                                              path: "user/add/:clientId",
                                              element: <AddUser />,
                                            },
                                            {
                                              path: "user/edit/:id",
                                              element: <EditUser />,
                                            },
                                          ]
                                        : []),
                                    ]
                                  : []),

                                ...(role === Role.SUPERADMIN ||
                                role === Role.ADMIN
                                  ? [{ path: "pricing", element: <Pricing /> }]
                                  : []),
                              ]
                            : []),
                        ]
                      : []),
                    ...(role === Role.SUPERADMIN || role === Role.ADMIN
                      ? [
                          {
                            path: "settings",
                            element: <Settings />,
                          },
                          {
                            path: "wallet",
                            element: <Wallet />,
                          },
                        ]
                      : []),
                  ]
                : [
                    {
                      path: "*",
                      element:
                        role === Role.SUPERADMIN ||
                        role === Role.ADMIN ||
                        role === Role.MANAGER ? (
                          <Pricing />
                        ) : (
                          <Page404 />
                        ),
                    },
                  ]),

              // { path: "products", element: <ProductsPage /> },
              // { path: "blog", element: <BlogPage /> },
            ],
          },
        ]
      : []),
    {
      path: "login",
      element: <LoginPage />,
    },
    {
      path: "register",
      element: <RegisterPage />,
    },
    {
      path: "fpass",
      element: <ForgotPassword />,
    },
    {
      path: "verify/:id",
      element: <VerifyClient />,
    },
    {
      element: <SimpleLayout />,
      children: [
        {
          path: "feedback/:type/:clientId",
          element: <NewFeedback />,
        },
      ],
    },
    ...(role && role.length > 0
      ? [
          {
            element: <SimpleLayout />,
            children: [
              { element: <Navigate to="/dashboard" />, index: true },
              { path: "404", element: <Page404 /> },
              { path: "*", element: <Navigate to="/404" /> },
            ],
          },
          {
            path: "*",
            element: <Navigate to="/404" replace />,
          },
        ]
      : [
          {
            path: "*",
            element: <Navigate to="/login" replace />,
          },
        ]),
  ]);
  return <Suspense fallback={<Loader />}>{routes}</Suspense>;
};
